import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircleOutline as PostIcon,
  DeleteOutline as DeleteIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import SalesImportDeprecationBanner from '../../../../components/venue/SalesImportDeprecationBanner';
import salesImportService from '../../../../services/salesImportService';
import type {
  SalesImport,
  SalesImportLine,
  SalesImportLineStatus,
  SalesImportStatus,
} from '../../../../types/salesImport';

const formatDateTime = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_LABEL: Record<SalesImportStatus, string> = {
  draft: 'Draft',
  posted: 'Posted',
  failed: 'Failed',
};

const STATUS_COLOR: Record<SalesImportStatus, 'default' | 'success' | 'error'> = {
  draft: 'default',
  posted: 'success',
  failed: 'error',
};

const LINE_STATUS_LABEL: Record<SalesImportLineStatus, string> = {
  pending: 'Pending',
  posted: 'Posted',
  skipped: 'Skipped',
  failed: 'Failed',
};

const LINE_STATUS_COLOR: Record<
  SalesImportLineStatus,
  'default' | 'success' | 'warning' | 'error'
> = {
  pending: 'default',
  posted: 'success',
  skipped: 'warning',
  failed: 'error',
};

const SalesImportDetail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const { importId } = useParams<{ importId: string }>();
  const base = `/admin/venues/${venueId}/sales-imports`;

  const [importDoc, setImportDoc] = useState<SalesImport | null>(null);
  const [lines, setLines] = useState<SalesImportLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const extractErr = (err: any): string =>
    err?.response?.data?.error?.message ??
    err?.response?.data?.message ??
    err?.message ??
    'Unexpected error';

  const load = useCallback(async () => {
    if (!venueId || !importId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await salesImportService.get(venueId, importId);
      const doc = (res.salesImport ?? res.import) as SalesImport | undefined;
      setImportDoc(doc ?? null);
      setLines(res.lines ?? []);
    } catch (err: any) {
      setError(extractErr(err));
    } finally {
      setLoading(false);
    }
  }, [venueId, importId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePost = async () => {
    if (!importId) return;
    setPosting(true);
    setError(null);
    try {
      const res = await salesImportService.post(venueId, importId);
      const doc = (res.salesImport ?? res.import) as SalesImport | undefined;
      if (doc) setImportDoc(doc);
      if (res.lines) setLines(res.lines);
      setConfirmOpen(false);
    } catch (err: any) {
      setError(extractErr(err));
      setConfirmOpen(false);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async () => {
    if (!importId) return;
    if (!window.confirm('Delete this draft sales import? This cannot be undone.')) return;
    setDeleting(true);
    setError(null);
    try {
      await salesImportService.remove(venueId, importId);
      navigate(base);
    } catch (err: any) {
      setError(extractErr(err));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!importDoc) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error ?? 'Sales import not found.'}</Alert>
        <Button onClick={() => navigate(base)} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Sales Imports
        </Button>
      </Box>
    );
  }

  const isDraft = importDoc.status === 'draft';
  const isPosted = importDoc.status === 'posted';
  const hasFailed = importDoc.failedLineCount > 0;
  const canPost = isDraft && !hasFailed;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate(base)} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          Sales Import
        </Typography>
        <Chip
          label={STATUS_LABEL[importDoc.status]}
          size="small"
          color={STATUS_COLOR[importDoc.status]}
          sx={{ ml: 1, fontWeight: 500 }}
        />
        <Box sx={{ flex: 1 }} />
        {isDraft && (
          <Tooltip title="Delete draft">
            <span>
              <IconButton
                onClick={handleDelete}
                disabled={deleting}
                sx={{ color: theme.palette.text.secondary }}
              >
                {deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {isDraft && admin && (
          <Button
            variant="contained"
            startIcon={<PostIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={!canPost}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Post Import
          </Button>
        )}
      </Box>

      {/* INVENTORY-TRUST.1c — retirement context, shown while a draft can still act. */}
      {isDraft && <SalesImportDeprecationBanner isAdmin={admin} sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header card */}
      <Paper sx={{ borderRadius: 2, mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          divider={<Divider orientation="vertical" flexItem />}
          spacing={0}
          sx={{ px: 3, py: 2.5 }}
        >
          <Box sx={{ flex: 1, pr: { sm: 3 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Source file
            </Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
              {importDoc.sourceFilename || '—'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, px: { sm: 3 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Created
            </Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
              {formatDateTime(importDoc.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, pl: { sm: 3 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
              Posted
            </Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>
              {formatDateTime(importDoc.postedAt)}
            </Typography>
            {importDoc.postedBy && (
              <Typography variant="caption" color="text.secondary">
                by {importDoc.postedBy}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Status banner — one-glance read of what state this import is in
          and what (if anything) the operator still needs to do. */}
      {isDraft && !hasFailed && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Draft.</strong> Review the lines below, then post when ready.
        </Alert>
      )}
      {isDraft && hasFailed && (
        <Alert severity="error" variant="filled" icon={<ErrorIcon />} sx={{ mb: 3 }}>
          <strong>
            {importDoc.failedLineCount} {importDoc.failedLineCount === 1 ? 'row' : 'rows'} failed.
          </strong>{' '}
          This import cannot be posted. Delete it and upload a corrected file.
        </Alert>
      )}
      {isPosted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>Posted{importDoc.postedAt ? ` ${formatDateTime(importDoc.postedAt)}` : ''}.</strong>{' '}
          Sales Imports generate standard SALE inventory movements.
        </Alert>
      )}
      {importDoc.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
          <strong>Posting failed.</strong> No inventory changes were made.
        </Alert>
      )}

      {/* Summary cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {(
          [
            { label: 'Total rows', value: importDoc.rowCount, tone: undefined },
            { label: 'Posted', value: importDoc.postedLineCount, tone: 'success' as const },
            { label: 'Skipped', value: importDoc.skippedLineCount, tone: 'warning' as const },
            { label: 'Failed', value: importDoc.failedLineCount, tone: 'error' as const },
          ]
        ).map((c) => {
          const tone =
            c.tone === 'error'
              ? theme.palette.error.main
              : c.tone === 'warning'
                ? theme.palette.warning.main
                : c.tone === 'success'
                  ? theme.palette.success.main
                  : theme.palette.text.primary;
          return (
            <Paper
              key={c.label}
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                flex: 1,
                borderRadius: 2,
                borderColor:
                  c.tone && c.value > 0 ? alpha(tone, 0.4) : alpha(theme.palette.divider, 0.5),
                bgcolor: c.tone && c.value > 0 ? alpha(tone, 0.05) : 'transparent',
              }}
            >
              <Typography
                variant="caption"
                sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 600 }}
              >
                {c.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: c.tone && c.value > 0 ? tone : 'text.primary',
                  mt: 0.5,
                }}
              >
                {c.value}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      {/* Lines table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Lines
          </Typography>
          {importDoc.failedLineCount > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${importDoc.failedLineCount} failed`}
              size="small"
              color="error"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Row', 'Item', 'Qty', 'Mode', 'Status', 'Issue'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">No lines.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                // Failed rows surface first so the operator's first eye-stop is
                // the work they need to do; everything else keeps source order.
                [...lines]
                  .sort((a, b) => {
                    if (a.status === 'failed' && b.status !== 'failed') return -1;
                    if (b.status === 'failed' && a.status !== 'failed') return 1;
                    return a.rowIndex - b.rowIndex;
                  })
                  .map((l) => {
                    const isFailed = l.status === 'failed';
                    const isSkipped = l.status === 'skipped';
                    const itemName = l.displayItemName?.trim() || l.posMenuItemRef;
                    const showRefBeneath = !!l.displayItemName?.trim();
                    return (
                      <TableRow
                        key={l._id}
                        sx={{
                          bgcolor: isFailed
                            ? alpha(theme.palette.error.main, 0.08)
                            : isSkipped
                              ? alpha(theme.palette.warning.main, 0.04)
                              : undefined,
                          borderLeft: isFailed
                            ? `3px solid ${theme.palette.error.main}`
                            : '3px solid transparent',
                        }}
                      >
                        <TableCell sx={{ width: 56 }}>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            {l.rowIndex}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ lineHeight: 1.3, color: isFailed ? 'error.main' : 'text.primary' }}
                          >
                            {itemName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                            {l.displayServingSize && (
                              <Chip
                                label={l.displayServingSize}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 500 }}
                              />
                            )}
                            {showRefBeneath && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                              >
                                {l.posMenuItemRef}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: 80 }}>
                          <Typography variant="body1" fontWeight={700}>
                            {l.quantitySold}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 80 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {l.resolution?.mode ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 100 }}>
                          <Chip
                            size="small"
                            label={LINE_STATUS_LABEL[l.status]}
                            color={LINE_STATUS_COLOR[l.status]}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          {isFailed ? (
                            <Box>
                              <Typography variant="body2" color="error.main" fontWeight={500}>
                                {l.failureMessage ?? l.failureCode ?? 'Failed'}
                              </Typography>
                              {l.failureCode && (
                                <Tooltip title="Failure code (for support)">
                                  <Typography
                                    variant="caption"
                                    color="error.dark"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}
                                  >
                                    {l.failureCode}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Post confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => !posting && setConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Post Sales Import?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This will create SALE inventory movements and update stock balances.
            <Box component="p" sx={{ mt: 1.5, mb: 0, fontWeight: 600, color: 'error.main' }}>
              Manual sales import is retired. If this file covers a period the POS integration
              already recorded, posting it will deplete that stock a second time.
            </Box>
            <Box component="p" sx={{ mt: 1.5, mb: 0 }}>
              Only continue for a period the POS did not cover.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={posting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePost}
            variant="contained"
            disabled={posting}
            startIcon={posting ? <CircularProgress size={16} color="inherit" /> : <PostIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Post
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesImportDetail;
